import { getApiClient } from "@src/api";
import { graphql } from "@src/gql";
import type { FeatureEntity, SupportedFeature } from "@src/gql/graphql";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import type { AnyFeatureState } from "../plan-schemas";
import { anyFeatureStateSchema } from "../plan-schemas";
import { defaultMeteredPerEntityState } from "./methods";
import { planQueryKeys } from "./query-keys";

const fetchPlanEntityMetered = graphql(/* GraphQL */ `
	query MyPlanMetered($whiteboardId: String!, $entityType: FeatureEntity!, $supportedFeature: SupportedFeature!) {
		plans {
			featureState(
				entityId:  {
					id: $whiteboardId,
					type: $entityType
				}
				feature: $supportedFeature
			) {
				...FeatureStateFragment
			}
		}
	}
`);

// Type-safe query function
export async function fetchEntityMeteredData(
	entityId: string,
	entityType: FeatureEntity,
	supportedFeature: SupportedFeature,
): Promise<AnyFeatureState> {
	const client = getApiClient();

	const response = await client.query({
		query: fetchPlanEntityMetered,
		variables: { whiteboardId: entityId, entityType, supportedFeature },
	});

	const featureState = response.data?.plans.featureState;
	if (!featureState) {
		return defaultMeteredPerEntityState(supportedFeature);
	}

	return anyFeatureStateSchema.parse(featureState);
}

export const useMyPlanEntityMetered = (
	entityId: string,
	entityType: FeatureEntity,
	supportedFeature: SupportedFeature,
) =>
	useSuspenseQuery({
		queryKey: planQueryKeys.myPlanEntityMetered(
			entityId,
			entityType,
			supportedFeature,
		),
		queryFn: () =>
			fetchEntityMeteredData(entityId, entityType, supportedFeature),
	});

export const useMyPlanEntityMeteredList = (
	configs: Array<{
		entityId: string;
		entityType: FeatureEntity;
		supportedFeature: SupportedFeature;
	}>,
) => {
	if (configs.length === 0) {
		return [];
	}

	return useQueries({
		queries: configs.map(({ entityId, entityType, supportedFeature }) => ({
			queryKey: planQueryKeys.myPlanEntityMetered(
				entityId,
				entityType,
				supportedFeature,
			),
			queryFn: () =>
				fetchEntityMeteredData(entityId, entityType, supportedFeature),
		})),
	});
};
